import { combineEpics, ofType } from 'redux-observable'
import { Observable } from 'rxjs'
import { interval } from 'rxjs/observable/interval'
import { catchError, mergeMap, switchMap, takeWhile } from 'rxjs/operators'

import { removeUndefinedValues } from 'utils/helpers'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  selectActualFeatureFlags,
  selectPartner,
  selectSelfCompanyId,
} from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { MINIMAL_ALLOWED_POLLING_INTERVAL } from 'containers/Polling/constants'
import { addToast } from 'containers/Toasts/actions'

import {
  createIncidentError,
  createIncidentSuccess,
  createPresetSuccess,
  fetchFiltersError,
  fetchFiltersSuccess,
  fetchLogsEntireCountError,
  fetchLogsEntireCountSuccess,
  fetchMitigationDataError,
  fetchMitigationDataSuccess,
  fetchPresetsSuccess,
  fetchThreatLogDataError,
  fetchThreatLogDataSuccess,
  preparePayload,
  releaseFromInvestigationError,
  releaseFromInvestigationSuccess,
  releaseFromSpamError,
  releaseFromSpamSuccess,
  removePresetSuccess,
  setAllFilters,
  setFilter,
  setLoader,
  updatePresetSuccess,
} from './actions'
import {
  allowListErrorMessage,
  CREATE_INCIDENT,
  CREATE_PRESET_ASYNC,
  createIncidentErrorCodes,
  FETCH_DIRECTORY_TAGS,
  FETCH_FILTERS,
  FETCH_LOGS_ENTIRE_COUNT,
  FETCH_MITIGATION_DATA_ASYNC,
  FETCH_PRESETS_ASYNC,
  FETCH_THREAT_LOG_DATA,
  PAGINATE_CHANGE,
  RELEASE_FROM_INVESTIGATION_ASYNC,
  RELEASE_FROM_MS_QUARANTINE_ASYNC,
  RELEASE_FROM_SPAM_ASYNC,
  REMOVE_PRESET_ASYNC,
  RESET_ALL_FILTERS,
  SET_ALL_FILTERS,
  SET_ARRIVAL_DATE_FILTER,
  SET_FILTER,
  SET_LOG_DATE_FILTER,
  UPDATE_PRESET_ASYNC,
} from './constants'
import {
  selectAllFilters,
  selectDirectoryTagsPage,
  selectDirectoryTagsSearch,
  selectFilteredThreatLogFilters,
  selectFiltersLoader,
  selectIsPresetsLoading,
  selectReleaseFromSpamData,
  selectSavedFilters,
  selectSavedSelectedFilter,
  selectThreatLogFilters,
} from './selectors'
import {
  determineSelectedFilter,
  isFiltersEqual,
  prepareCompanyIds,
  prepareFiltersToBe,
} from './utils'

export const shouldPreventApiCall = (state) => {
  const filters = selectThreatLogFilters(state)
  const ff = selectActualFeatureFlags(state)

  return filters.log_type?.includes('Presumed Safe')
    ? ff.REMOVE_PRESUMED_SAFE_LIMIT
      ? false
      : !(filters.subject || filters.sender)
    : false
}

const fetchFiltersEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_FILTERS.INITIATE, FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS).mergeMap(() => {
    const state = store.getState()
    return api.resolutionCenter
      .fetchThreatLogFilters(
        state.partnerOptions.selectedCompany
          ? state.partnerOptions.selectedCompany.value
          : state.app.data.id
      )
      .map((response) => fetchFiltersSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchFiltersError, error)))
  })

const fetchThreatLogDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      SET_ALL_FILTERS,
      SET_LOG_DATE_FILTER,
      SET_ARRIVAL_DATE_FILTER,
      SET_FILTER,
      RESET_ALL_FILTERS,
      PAGINATE_CHANGE,
      RELEASE_FROM_MS_QUARANTINE_ASYNC.SUCCESS,
      RELEASE_FROM_INVESTIGATION_ASYNC.SUCCESS,
      RELEASE_FROM_INVESTIGATION_ASYNC.ERROR,
      FETCH_FILTERS.SUCCESS,
      FETCH_PRESETS_ASYNC.SUCCESS
    )
    .filter((action) => {
      if (!matchURL(URL_CONST.investigation.threatLog)) return false
      const state = store.getState()
      const isPresetsLoading = selectIsPresetsLoading(state)
      if (action.type === FETCH_FILTERS.SUCCESS && isPresetsLoading) return false
      const shouldPreventCall = shouldPreventApiCall(state) || action.payload?.preventApiCall
      const isLoading = selectFiltersLoader(state)
      const shouldDisableLoader = isLoading ? false : shouldPreventCall
      if (shouldDisableLoader) {
        store.dispatch(setLoader(false))
        return false
      }
      return !isLoading && !shouldPreventCall
    })
    .switchMap(() => {
      const state = store.getState()
      const filteredFilters = selectFilteredThreatLogFilters(state)
      return api.resolutionCenter
        .fetchThreatLogData(filteredFilters)
        .map((response) => fetchThreatLogDataSuccess(response.response))
        .catch((error) => Observable.of(epicsErrorHandler(fetchThreatLogDataError, error, [403])))
        .takeUntil(action$.ofType(FETCH_THREAT_LOG_DATA.CANCEL))
    })

const createIncidentEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(CREATE_INCIDENT.INITIATE)
    .filter(() => matchURL(URL_CONST.investigation.threatLog))
    .switchMap((action) => {
      const body = {
        gmail_message_id: action.payload.gmail_message_id,
        office365_message_id: action.payload.office365_message_id,
        message_id: action.payload.message_id,
        sender_address: action.payload.sender_address,
        recipient_address: action.payload.recipient_address,
        subject: action.payload.subject,
        from: 'Investigation page',
        ignore_allow_list: action.payload.ignoreAllowList,
      }
      return api.threatLog
        .createIncident(
          action.payload.incidentCreator,
          action.payload.eid,
          removeUndefinedValues(body)
        )
        .switchMap((response) =>
          Observable.of(
            createIncidentSuccess(action.payload.eid, response.response.incidentId),
            addToast({
              id: 'Create_Incident_Toast_Message_Success',
              button: {
                text: 'Go to incident',
                id: 'Create_Incident_Toast_Message_Go_To_Incident_Button',
                onClick: () => {
                  window.location = `${URL_CONST.incidents.attackdetails}/${response?.response.incidentId}`
                },
              },
              message: (
                <span id="Create_Incident_Toast_Message">
                  Incident
                  <a
                    className="white cp b"
                    href={`${URL_CONST.incidents.attackdetails}/${response?.response.incidentId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {` IS-${response?.response.incidentId} `}
                  </a>
                  has been successfully created
                </span>
              ),
            })
          )
        )
        .catch((error) => {
          const errorActions = [
            epicsErrorHandler(
              () => createIncidentError(error, action.payload),
              error,
              createIncidentErrorCodes
            ),
          ]

          const FORBIDDEN_STATUS_CODE = 403

          // Add toast only if its not allow list case
          if (
            !(
              error.status === FORBIDDEN_STATUS_CODE &&
              error.response?.message === allowListErrorMessage
            )
          ) {
            errorActions.push(
              addToast({
                message: error.response?.message,
                type: 'error',
                id: 'Create_Incident_Toast_Message_Error',
              })
            )
          }

          return Observable.of(...errorActions)
        })
        .takeUntil(action$.ofType(CREATE_INCIDENT.CANCEL))
    })

const fetchLogsEntireCount = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_LOGS_ENTIRE_COUNT.INITIATE).mergeMap(() =>
    api.resolutionCenter
      .fetchLogsEntireCount(
        store.getState().partnerOptions.selectedCompany?.value,
        selectFilteredThreatLogFilters(store.getState())
      )
      .map((response) => fetchLogsEntireCountSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchLogsEntireCountError, error)))
      .takeUntil(action$.ofType(FETCH_LOGS_ENTIRE_COUNT.CANCEL))
  )

const fetchMitigationDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_MITIGATION_DATA_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .getMitigationData(
        action.payload.company_id,
        action.payload.gmail_message_id,
        action.payload.office365_message_id,
        action.payload.message_id,
        action.payload.recipient_address,
        action.payload.sender_address,
        action.payload.subject,
        action.payload.eid || action.payload.new_eid
      )
      .map((response) => fetchMitigationDataSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchMitigationDataError, error)))
      .takeUntil(action$.ofType(FETCH_MITIGATION_DATA_ASYNC.CANCEL))
  )

const createPresetEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CREATE_PRESET_ASYNC.INITIATE).mergeMap((action) => {
    const { presetId, presetName, isChosenDefault, filters } = action.payload
    const state = store.getState()
    const partner = selectPartner(state)
    const companyFilter = selectCompanyFilter(state)
    const selfCompanyId = selectSelfCompanyId(state)
    const company_ids = prepareCompanyIds(partner, companyFilter, selfCompanyId)

    const body = {
      presetId,
      presetName,
      isChosenDefault,
      company_ids,
      ...prepareFiltersToBe(filters),
    }

    return api.resolutionCenter
      .createPreset(body)
      .mergeMap((response) =>
        Observable.of(
          createPresetSuccess(response),
          addToast({
            id: 'Create_Preset_Toast_Message_Success',
            message: <span id="Create_Preset_Toast_Message">Filter successfully created</span>,
          })
        )
      )
      .catch((error) =>
        Observable.of(
          addToast({
            type: 'error',
            id: 'Create_Preset_Toast_Message_Error',
            message: <span id="Create_Preset_Toast_Message">Filter was not saved</span>,
          }),
          epicsErrorHandler(fetchMitigationDataError, error)
        )
      )
      .takeUntil(action$.ofType(CREATE_PRESET_ASYNC.CANCEL))
  })

const updatePresetEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(UPDATE_PRESET_ASYNC.INITIATE).mergeMap((action) => {
    const state = store.getState()
    const currentState = selectSavedFilters(state)[action.payload.presetId]
    const partner = selectPartner(state)
    const companyFilter = selectCompanyFilter(state)
    const selfCompanyId = selectSelfCompanyId(state)
    const company_ids = prepareCompanyIds(partner, companyFilter, selfCompanyId)
    const filterWasUpdated = !action.payload.isChosenDefault && !action.payload.presetName

    const {
      presetId,
      presetName = currentState.presetName,
      isChosenDefault = currentState.isChosenDefault,
      filters,
    } = action.payload

    const body = {
      presetId,
      presetName,
      isChosenDefault,
      company_ids,
      ...prepareFiltersToBe(!filterWasUpdated ? { ...currentState.filters, ...filters } : filters),
    }

    return api.resolutionCenter
      .updatePreset(body)
      .mergeMap((response) =>
        Observable.of(
          updatePresetSuccess(response),
          addToast({
            id: 'Update_Preset_Toast_Message_Success',
            message: <span id="Update_Preset_Toast_Message">Filter successfully updated</span>,
          })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(fetchMitigationDataError, error),
          addToast({
            type: 'error',
            id: 'Update_Preset_Toast_Message_Error',
            message: <span id="Update_Preset_Toast_Message">Filter was not updated</span>,
          })
        )
      )
      .takeUntil(action$.ofType(UPDATE_PRESET_ASYNC.CANCEL))
  })

const fetchPresetsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      FETCH_PRESETS_ASYNC.INITIATE,
      CREATE_PRESET_ASYNC.SUCCESS,
      UPDATE_PRESET_ASYNC.SUCCESS,
      REMOVE_PRESET_ASYNC.SUCCESS
    )
    .switchMap(() =>
      api.resolutionCenter
        .fetchPresets()
        .mergeMap((response) => {
          const state = store.getState()
          const payload = preparePayload(response)
          const currentFilters = selectAllFilters(state)
          const selectedFilter = determineSelectedFilter(
            state?.investigation?.self,
            payload.savedFilters,
            payload.defaultFilter,
            payload.filterFromUrl
          )
          const newFilter = selectedFilter && payload.savedFilters[selectedFilter].filters
          const shouldBeUpdated = newFilter && !isFiltersEqual(currentFilters, newFilter)
          if (shouldBeUpdated && selectedFilter) {
            return Observable.of(setAllFilters(newFilter), fetchPresetsSuccess(payload))
          }
          return Observable.of(fetchPresetsSuccess(payload))
        })
        .catch((error) => Observable.of(epicsErrorHandler(fetchMitigationDataError, error)))
        .takeUntil(action$.ofType(FETCH_PRESETS_ASYNC.CANCEL))
    )

const removePresetEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(REMOVE_PRESET_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .removePreset({ presetId: action.payload.presetId })
      .mergeMap((response) =>
        Observable.of(
          addToast({
            id: 'Remove_Preset_Toast_Message_Success',
            message: <span id="Remove_Preset_Toast_Message">Filter successfully removed</span>,
          }),
          removePresetSuccess(response)
        )
      )
      .catch((error) =>
        Observable.of(
          addToast({
            type: 'error',
            id: 'Remove_Preset_Toast_Message_Error',
            message: <span id="Remove_Preset_Toast_Message">Filter was not removed</span>,
          }),
          epicsErrorHandler(fetchMitigationDataError, error)
        )
      )
      .takeUntil(action$.ofType(REMOVE_PRESET_ASYNC.CANCEL))
  )

const releaseMSQuarantineEmailEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RELEASE_FROM_MS_QUARANTINE_ASYNC.INITIATE),
    mergeMap((action) => {
      store.dispatch(
        addToast({
          type: 'info',
          id: 'Microsoft_email_released_Info',
          message: <span id="Microsoft_email_released">Request is in progress</span>,
        })
      )
      return api.settings
        .releaseMSQuarantineEmail(
          action.payload.messageId,
          action.payload.newId,
          action.payload.recipientAddress,
          action.payload.index
        )
        .pipe(
          switchMap(({ response }) => {
            const { task_id: taskId } = response
            const pollingStream$ = interval(MINIMAL_ALLOWED_POLLING_INTERVAL).pipe(
              switchMap(() => api.settings.releaseMSQuarantineEmailStatus(taskId)),
              mergeMap((statusResponse) => {
                if (statusResponse.task_status === 'finished') {
                  return Observable.of(
                    addToast({
                      id: 'Microsoft_email_released_Success',
                      message: (
                        <span id="Microsoft_email_released">
                          The email released from Microsoft quarantine
                        </span>
                      ),
                    }),
                    RELEASE_FROM_MS_QUARANTINE_ASYNC.onSuccess(action.payload.messageId)
                  )
                } else if (
                  statusResponse.task_status === 'failed' ||
                  statusResponse.statusCode >= 400
                ) {
                  return Observable.of(
                    addToast({
                      type: 'error',
                      id: 'Microsoft_email_released_Error',
                      message: (
                        <span id="Microsoft_email_released">
                          Something went wrong. Please try again
                        </span>
                      ),
                    }),
                    RELEASE_FROM_MS_QUARANTINE_ASYNC.onError(action.payload.messageId)
                  )
                }
                return Observable.empty() // Emit nothing to continue polling
              }),
              takeWhile(
                (z) =>
                  z.type !== RELEASE_FROM_MS_QUARANTINE_ASYNC.SUCCESS &&
                  z.type !== RELEASE_FROM_MS_QUARANTINE_ASYNC.ERROR
              ), // Correct takeWhile condition
              catchError((error) =>
                Observable.of(
                  addToast({
                    type: 'error',
                    id: 'Microsoft_email_released_Error',
                    message: (
                      <span id="Microsoft_email_released">
                        Something went wrong. Please try again
                      </span>
                    ),
                  }),
                  RELEASE_FROM_MS_QUARANTINE_ASYNC.onError(action.payload.messageId),
                  console.log(error)
                )
              ) // Handle errors within the polling stream
            )
            return pollingStream$
          })
        )
    })
  )

const releaseFromInvestigationEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(RELEASE_FROM_INVESTIGATION_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .releaseFromInvestigation({
        id: action.payload.id,
        index: action.payload.index,
      })
      .mergeMap(() =>
        Observable.of(
          releaseFromInvestigationSuccess(),
          addToast({
            id: 'Release_Email_Toast_Message_Success',
            message: (
              <span id="Release_Email_Toast_Message_Success">
                Email has been successfully released.
              </span>
            ),
          })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(releaseFromInvestigationError, error),
          addToast({
            type: 'error',
            id: 'Release_Email_Toast_Message_Error',
            message: (
              <div style={{ display: 'flex', flexDirection: 'column', width: '285px' }}>
                <span style={{ fontWeight: 600 }}>Unable to release email.</span>
                <span>We encountered an issue while attempting to release this email.</span>
              </div>
            ),
          })
        )
      )
      .takeUntil(action$.ofType(RELEASE_FROM_INVESTIGATION_ASYNC.CANCEL))
  )

const releaseFromSpamEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(RELEASE_FROM_SPAM_ASYNC.INITIATE).mergeMap(() => {
    const state = store.getState()
    const data = selectReleaseFromSpamData(state)
    return api.threatLog
      .releaseFromSpam(data)
      .mergeMap((response) =>
        Observable.of(
          addToast({
            id: 'Release_Spam_Toast_Message_Success',
            message: (
              <span id="Release_Spam_Toast_Message">Bulk release from Spam is in progress</span>
            ),
          }),
          releaseFromSpamSuccess(response)
        )
      )
      .catch((error) =>
        Observable.of(
          addToast({
            type: 'error',
            id: 'Release_Spam_Toast_Message_Error',
            message: <span id="Release_Spam_Toast_Message">Bulk release from Spam failed</span>,
          }),
          epicsErrorHandler(releaseFromSpamError, error)
        )
      )
      .takeUntil(action$.ofType(RELEASE_FROM_SPAM_ASYNC.CANCEL))
  })

const fetchDirectoryTagsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      FETCH_DIRECTORY_TAGS.INITIATE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
      // SET_TAGS_PAGE,
      // SET_TAGS_SEARCH
    )
    .filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.investigation.threatLog)
      return true
    })
    .mergeMap((action) => {
      const state = store.getState()
      const companyFilter = selectCompanyFilter(state)
      const tagsPage = selectDirectoryTagsPage(state)
      const tagsSearch = selectDirectoryTagsSearch(state)
      return api.mailboxes
        .getTags({
          companyId: companyFilter?.value,
          onlyAutoTags: true,
          page: tagsPage,
          search: tagsSearch,
          searchByTagIds: Array.isArray(action.payload) ? action.payload : undefined,
          allTags: false,
        })
        .mergeMap((response) => {
          const actions = [FETCH_DIRECTORY_TAGS.onSuccess(response)]

          // Check if we had missing tag IDs and got empty response
          // Meaning we have unavailable tags, and we should remove them from the filter
          if (
            action.payload &&
            Array.isArray(action.payload) &&
            action.payload.length > 0 &&
            (!response.tags || response.tags.length === 0)
          ) {
            const selectedFilter = selectSavedSelectedFilter(state)

            actions.push(
              setFilter('tagIds', []),
              addToast({
                type: 'warning',
                id: 'Directory_Tags_Missing_Warning',
                message: (
                  <span id="Directory_Tags_Missing_Message">
                    The &quot;{selectedFilter?.name || 'Selected'}&quot; filter includes
                    company-specific parameters and has been reset. Please choose a new filter or
                    switch to the correct company.
                  </span>
                ),
              })
            )
          }

          return Observable.of(...actions)
        })
        .catch((error) => Observable.of(epicsErrorHandler(FETCH_DIRECTORY_TAGS.onError, error)))
        .takeUntil(action$.ofType(FETCH_DIRECTORY_TAGS.CANCEL))
    })

export default combineEpics(
  fetchFiltersEpic,
  fetchThreatLogDataEpic,
  createIncidentEpic,
  fetchLogsEntireCount,
  fetchMitigationDataEpic,
  createPresetEpic,
  updatePresetEpic,
  fetchPresetsEpic,
  removePresetEpic,
  releaseMSQuarantineEmailEpic,
  releaseFromSpamEpic,
  releaseFromInvestigationEpic,
  fetchDirectoryTagsEpic
)
